"use strict";

import config from "@/config.json";
import AppError from "@/utils/AppError.js";

/**
 * State object
 */
const initialState = () => ({
  token: "",
  user: null
});
const state = initialState();

// Getter functions
const getters = {
  isAuthenticated: state => !!state.token,
  getUser: state => state.user
};

// Actions
const actions = {
  /**
   * Reset
   */
  reset({ commit }) {
    commit("reset");
  },
  /**
   * Login
   */
  login({ commit, dispatch }, requestData) {
    return new Promise((resolve, reject) => {
      dispatch("Util/startLoading", null, { root: true });
      window
        .api({
          url: config.api.route.authLogin,
          data: requestData,
          method: "POST"
        })
        .then(resp => {
          const { token, user } = resp.data;
          localStorage.setItem("auth-token", token);
          window.api.defaults.headers.common["Authorization"] = token;
          commit("authSuccess", { token, user });
          resolve(user);
        })
        .catch(err => {
          dispatch("logout");
          reject(err);
        })
        .finally(() => {
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  },
  /**
   * Auto login
   */
  autoLogin({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        reject(new AppError("NO_TOKEN", null, true, false));
        return;
      }

      dispatch("Util/startLoading", null, { root: true });
      window.api.defaults.headers.common["Authorization"] = token;
      window
        .api({
          url: config.api.route.authCheckToken,
          method: "GET"
        })
        .then(resp => {
          const { user } = resp.data;
          commit("authSuccess", { token, user });
          resolve(user);
        })
        .catch(err => {
          dispatch("logout");
          reject(err);
        })
        .finally(() => {
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  },
  /**
   * Logout
   */
  logout({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      if (
        typeof window.api.defaults.headers.common["Authorization"] ===
        "undefined"
      ) {
        localStorage.removeItem("auth-token");
        commit("logout");
        resolve();
        return;
      }

      dispatch("Util/startLoading", null, { root: true });
      window
        .api({
          url: config.api.route.authLogout,
          method: "GET"
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          localStorage.removeItem("auth-token");
          delete window.api.defaults.headers.common["Authorization"];
          commit("logout");
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  },

  /**
   * Regist a volunteer
   */
  registerVolunteer({ dispatch }, requestData) {
    return new Promise((resolve, reject) => {
      dispatch("Util/startLoading", null, { root: true });
      const data = {
        email: requestData.email,
        password: requestData.password,
        type: 1,
        name: requestData.name,
        comments: requestData.comments,
        mobileNumber: requestData.mobileNumber,
        volunteer: {
          birthday: requestData.birthdate,
          motivations: requestData.motivations,
          motivationOther: requestData.motivationOther,
          interestAreadIds: requestData.interestAreadIds,
          course: requestData.course,
          schoolId: requestData.schoolId,
          ipsCommunityMemberId: requestData.ipsCommunityMember,
          ipsCommunityMemberDescription:
            requestData.ipsCommunityMemberDescription
        }
      };

      window
        .api({
          url: config.api.route.authVolunteerRegister,
          data: data,
          method: "POST"
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  },

  /**
   * Regist a entity
   */
  registerEntity({ dispatch }, requestData) {
    return new Promise((resolve, reject) => {
      dispatch("Util/startLoading", null, { root: true });

      const formData = new FormData();
      formData.append("email", requestData.email);
      formData.append("password", requestData.password);
      formData.append("type", requestData.type);
      formData.append("name", requestData.name);
      formData.append("namePerson", requestData.namePerson);
      formData.append("tel", requestData.mobileNumber);

      if (requestData.comments) {
        formData.append("comments", requestData.comments);
      }
      if (requestData.logo) {
        formData.append("entityLogo", requestData.logo);
      }

      window
        .api({
          url: config.api.route.authEntityRegister,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data"
          },
          method: "POST"
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  },

  /**
   * Change user's password
   */
  changePassword({ dispatch }, requestData) {
    return new Promise((resolve, reject) => {
      dispatch("Util/startLoading", null, { root: true });
      window
        .api({
          url: config.api.route.authChangePassword,
          data: requestData,
          method: "POST"
        })
        .then(() => {
          resolve();
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          dispatch("Util/stopLoading", null, { root: true });
        });
    });
  }
};

// Mutations
const mutations = {
  reset(state) {
    const newState = initialState();
    Object.keys(newState).forEach(key => {
      state[key] = newState[key];
    });
  },
  authSuccess(state, { token, user }) {
    state.token = token;
    state.user = user;
  },
  logout(state) {
    state.token = "";
    state.user = null;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
